import { RecurrenceConfigError } from './errors';
import { parseRecurrence } from './parse-recurrence';
import { previewWindows } from './preview-windows';
import type {
  Frequency,
  ScheduleConfig,
  Window,
  WindowOverlap,
} from './types';

/**
 * Windows to scan per frequency: the period after which the calendar pattern
 * driving day-of-month clamping repeats (48 months / 4 years — where both the
 * month-of-year and the leap-year phase realign), plus one because the
 * comparison is pairwise. Independent of `INTERVAL` — a larger interval never
 * needs more, so the flat count is a safe upper bound.
 *
 * DAILY and WEEKLY have no day-of-month clamp, so both seeds advance by the
 * same constant offset every period and the gap is identical throughout:
 * `next.WindowStart − current.WindowDue = (start − due) + interval × period`,
 * where `period` is one day for DAILY and seven for WEEKLY. An overlap
 * therefore exists at period 0→1 or never, making 2 a proof rather than the
 * approximation 49 is (see the century caveat below).
 *
 * No entry may exceed `MAX_PREVIEW_WINDOWS`: `previewWindows` throws a
 * `RangeError` past the cap, which the swallow below does not catch and callers
 * do not map to a validation error.
 */
export const SCAN_WINDOWS: Record<Frequency, number> = {
  DAILY: 2,
  WEEKLY: 2,
  MONTHLY: 49,
  YEARLY: 5,
};

/**
 * Detect whether a configuration would ever produce two occurrences whose
 * windows overlap, returning the first offending pair or `null`. The rule is
 * `next.WindowStart > current.WindowDue` for every consecutive pair.
 *
 * Checking the first two windows is not enough: the two seeds advance
 * independently, each with its own day-of-month clamp, so an overlap can first
 * appear a year in. Seeds 31 Jan / 28 Feb 2028 monthly are clean at period 0
 * (31 Feb clamps to 29 Feb, a leap year) but overlap at period 13 (clamps to
 * 28 Feb 2029). Hence the `SCAN_WINDOWS` horizon above.
 *
 * CAVEAT: the 48-month cycle ignores the leap rule's century exception (2100
 * is not a leap year), so a configuration straddling a skipped leap year could
 * overlap beyond this horizon.
 *
 * Throws (via `parseRecurrence`) on a malformed RRULE. Window-generation
 * failures are swallowed (see below), so a non-null return always means an
 * overlap.
 */
export const findWindowOverlap = (
  config: ScheduleConfig
): WindowOverlap | null => {
  // Outside the try: a bad FREQ or malformed component must still propagate.
  const { freq } = parseRecurrence(config.RRule);

  let windows: Window[];
  try {
    windows = previewWindows(config, SCAN_WINDOWS[freq]);
  } catch (error) {
    // A large INTERVAL can push a late period past the JS `Date` range, which
    // `expandFixed` reports as a RecurrenceConfigError. That is not an overlap,
    // so stop looking — the caller's own preview/materialisation call raises it
    // where it makes sense.
    if (error instanceof RecurrenceConfigError) {
      return null;
    }
    throw error;
  }

  for (let i = 0; i + 1 < windows.length; i++) {
    const current = windows[i]!;
    const next = windows[i + 1]!;
    if (next.WindowStart.getTime() <= current.WindowDue.getTime()) {
      return { current, next };
    }
  }

  return null;
};
